import React, { useState, useEffect, useContext } from 'react';
import { deleteEventById, eventControlAction, getEvent } from '../services/eventService';
import { EventContext } from '../context/EventContext';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';

const EventControlsPage: React.FC = () => {
    const { currentEvent, setCurrentEvent } = useContext(EventContext); // Event data in context
    const { addToast } = useToast();
    const [loading, setLoading] = useState<boolean>(false);
    const [showingDeletePopup, setShowingDeletePopup] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchEvent();
    }, []);

    const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmationText(e.target.value);
    };

    const [confirmationText, setConfirmationText] = useState('');

    const fetchEvent = async () => {
        try {
            if (currentEvent) {
                const response = await getEvent(currentEvent.event_id);
                setCurrentEvent(response);
            }
        } catch (error) {
            console.error("Error fetching event data", error);
            addToast("Failed to fetch event data.", true);
        }
    };

    const handleAction = async (actionEndpoint: string, successMessage: string) => {
        setLoading(true);

        if (currentEvent && !currentEvent.is_locked) {
            try {
                await eventControlAction(currentEvent.event_id, actionEndpoint);
                addToast(successMessage);
                fetchEvent(); // Refresh event data after action
            } catch (error) {
                console.error("Error performing action", error);
                addToast("Failed to perform action.", true);
            } finally {
                setLoading(false);
            }
        } else {
            addToast('Missing event or event is locked.', true);
            setLoading(false);
        }
    };

    const handleDeletePressed = async () => {
        setLoading(true);
        if (currentEvent) {
            try {
                const success = await deleteEventById(currentEvent.event_id);
                if (success) {
                    setShowingDeletePopup(false);
                    setCurrentEvent(null);
                    addToast('Event deleted successfully!');
                    addToast('Redirecting...');
                    navigate(ROUTES.HOME);
                } else {
                    addToast('Error deleting event. Please refresh the page and try again.', true);
                }
            } catch(error) {
                addToast('Error while deleting event. Please confirm you have the correct administrative privlidges.', true);
            } finally {
                setLoading(false);
            }
        } else {
            addToast('Missing event.', true);
        }

        setLoading(false);
    }


    return (
        <div>
            <div className="card" style={{ maxWidth: '900px' }}>
                <h2>{currentEvent ? `Event Controls for ${currentEvent.event_name}` : 'No event selected'}</h2>

                {/* Table of Event Controls */}
                {currentEvent && (
                    <div>
                        <table>
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Action : Toggle</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { label: 'Event', state: currentEvent.event_open, endpoint: 'open', closeEndpoint: 'close' },
                                    { label: 'Registration', state: currentEvent.registration_open, endpoint: 'open-registration', closeEndpoint: 'close-registration' },
                                    ...(currentEvent.has_checkin ? [{ label: 'Check-in', state: currentEvent.checkin_open, endpoint: 'open-checkin', closeEndpoint: 'close-checkin' }] : []),
                                    // { label: 'Confirmation', state: currentEvent.confirmation_open, endpoint: 'open-confirmation', closeEndpoint: 'close-confirmation' },
                                ].map(({ label, state, endpoint, closeEndpoint }) => (
                                    <tr key={label}>
                                        <td style={{ color: state ? 'black' : 'gray' }}>
                                            {label} {state ? 'Open' : 'Closed'}
                                        </td>
                                        <td>
                                            <button className="actionButton"
                                                onClick={() => handleAction(state ? closeEndpoint : endpoint, state ? `${label} Closed` : `${label} Opened`)}
                                                disabled={loading || currentEvent.is_locked}
                                                style={{
                                                    backgroundColor: state ? '#333' : '#888', // Darker when enabled, grayed when disabled
                                                }}
                                            >
                                                {state ? `Close ${label}` : `Open ${label}`}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <br />
                        <button className="warningButton" onClick={() => setShowingDeletePopup(true)} disabled={loading}>
                            Delete Event
                        </button>
                    </div>
                )}

            </div>

            {showingDeletePopup && (
                <div className="popOverBackdrop">
                    <div style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', height: '100vh' }}>
                        <div className='card' style={{ maxWidth: '600px', maxHeight: '500px', width: '90%' }}>
                            <h2>
                                Are you sure you want to delete{' '}
                                '{currentEvent?.event_name}' and all
                                of its data? This will not remove any fees owed for services rendered.
                            </h2>
                            <h3>This action <u>cannot</u> be undone.</h3>
                            <p>
                                Please type the word <i>delete</i> to continue with permanent removal.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', justifyContent: 'center' }}>
                                <input
                                    type='text'
                                    onChange={handleConfirmationChange}
                                    value={confirmationText}
                                />
                                <button
                                    className='warningButton'
                                    disabled={confirmationText !== 'delete'}
                                    style={{ height: '40px' }}
                                    onClick={() => { handleDeletePressed(); }}
                                >
                                    Confirm Delete
                                </button>
                            </div>
                            <div className='buttonsRow' style={{ marginTop: '10px' }}>
                                <button type='button' className='cancelButton' onClick={() => { setShowingDeletePopup(false); setConfirmationText(''); }}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};


export default EventControlsPage;
